import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ThirdPartyContactRow } from '@risksmart-app/trpc/src/types';
import type { GetThirdPartyContactsByThirdPartyIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps a TRPC contact to match the GraphQL query structure
 */
function mapTrpcContactToGraphQL(
  contact: ThirdPartyContactRow
): GetThirdPartyContactsByThirdPartyIdQuery['third_party_contact'][number] {
  return {
    Id: contact.Id,
    ThirdPartyId: contact.ThirdPartyId,
    Email: contact.Email,
    Name: contact.Name,
    JobTitle: contact.JobTitle,
    IsRevoked: contact.IsRevoked,
    user: contact.user,
  };
}

/**
 * Maps TRPC contacts data to match the GraphQL query structure
 */
export function mapTrpcContactsToGraphQL(
  trpcData: { contacts: ThirdPartyContactRow[] } | undefined
): GetThirdPartyContactsByThirdPartyIdQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    third_party_contact: trpcData.contacts.map(mapTrpcContactToGraphQL),
  };
}

export const useGetThirdPartyContactsTRPC = (
  thirdPartyId: string,
  includeRevoked: boolean,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.thirdPartyContact.list.queryOptions({
      thirdPartyId,
      isIncludingRevoked: includeRevoked,
    }),
    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: trpcError.message,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcContactsToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: trpcRefetch,
  };
};
